import { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [permissions, setPermissions] = useState({
    role: null,
    permissions: [],
    accessible_modules: [],
    is_admin: false,
    is_hr_assistant: false,
  });

  // Helper function to check if user has specific permission
  const hasPermission = (permission) => {
    if (permissions.is_admin) return true;
    if (permissions.permissions.includes("*")) return true;
    return permissions.permissions.includes(permission);
  };

  // Helper function to check if user can access a module
  const canAccessModule = (module) => {
    if (permissions.is_admin) return true;
    return permissions.accessible_modules.includes(module);
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
          
          // Fetch permissions
          try {
            const permsResponse = await api.get("/auth/permissions");
            setPermissions(permsResponse.data);
          } catch (e) {
            console.warn("Could not fetch permissions:", e);
          }
          
          // Fetch businesses
          const bizResponse = await api.get("/businesses");
          setBusinesses(bizResponse.data);
          
          if (response.data.current_business_id) {
            const current = bizResponse.data.find(b => b.id === response.data.current_business_id);
            setCurrentBusiness(current);
          }
        } catch (error) {
          console.error("Auth init error:", error);
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, [token]);

  const login = async (identifier, password) => {
    const response = await api.post("/auth/login", { identifier, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    
    // Fetch permissions after login
    try {
      const permsResponse = await api.get("/auth/permissions");
      setPermissions(permsResponse.data);
    } catch (e) {
      console.warn("Could not fetch permissions:", e);
    }
    
    // Fetch businesses after login
    const bizResponse = await api.get("/businesses");
    setBusinesses(bizResponse.data);
    
    if (userData.current_business_id) {
      const current = bizResponse.data.find(b => b.id === userData.current_business_id);
      setCurrentBusiness(current);
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post("/auth/register", userData);
    const { token: newToken, user: newUser } = response.data;
    
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setCurrentBusiness(null);
    setBusinesses([]);
    setPermissions({
      role: null,
      permissions: [],
      accessible_modules: [],
      is_admin: false,
      is_hr_assistant: false,
    });
  };

  const switchBusiness = async (businessId) => {
    await api.put(`/auth/switch-business/${businessId}`);
    const business = businesses.find(b => b.id === businessId);
    setCurrentBusiness(business);
    setUser(prev => ({ ...prev, current_business_id: businessId }));
  };

  const addBusiness = (business) => {
    setBusinesses(prev => [...prev, business]);
    setCurrentBusiness(business);
  };

  const removeBusiness = (businessId) => {
    setBusinesses(prev => {
      const updated = prev.filter(b => b.id !== businessId);
      // If we deleted the current business, switch to another one
      if (currentBusiness?.id === businessId && updated.length > 0) {
        setCurrentBusiness(updated[0]);
      }
      return updated;
    });
  };

  const updateBusiness = (updatedBiz) => {
    setBusinesses(prev => prev.map(b => b.id === updatedBiz.id ? updatedBiz : b));
    if (currentBusiness?.id === updatedBiz.id) {
      setCurrentBusiness(updatedBiz);
    }
  };

  const value = {
    user,
    token,
    loading,
    currentBusiness,
    businesses,
    permissions,
    hasPermission,
    canAccessModule,
    login,
    register,
    logout,
    switchBusiness,
    addBusiness,
    removeBusiness,
    updateBusiness,
    setUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
